/**
 * ContactTunnel Module
 *
 * Multi-step wizard that guides visitors through project qualification
 * before revealing the contact form. Coexists with ContactForm.js which
 * handles field validation on the final step.
 *
 * @module ContactTunnel
 */
export default class ContactTunnel {

  static DEFAULTS = {
    selectors: {
      tunnel: '[data-tunnel]',
      panel: '[data-tunnel-panel]',
      option: '[data-tunnel-option]',
      step: '[data-tunnel-step]',
      progressBar: '[data-tunnel-bar]',
      backBtn: '[data-tunnel-back]',
    },
    autoAdvanceDelay: 300,
  };

  constructor(options = {}) {
    this.config = { ...ContactTunnel.DEFAULTS, ...options };

    this.tunnel = document.querySelector(this.config.selectors.tunnel);
    if (!this.tunnel) return;

    this.panels = Array.from(this.tunnel.querySelectorAll(this.config.selectors.panel));
    this.steps = Array.from(this.tunnel.querySelectorAll(this.config.selectors.step));
    this.progressBar = this.tunnel.querySelector(this.config.selectors.progressBar);
    this.backBtn = this.tunnel.querySelector(this.config.selectors.backBtn);

    this.currentIndex = 0;
    this.answers = {};

    this._init();
  }

  /* ------------------------------------------------------------------ */

  _init() {
    this._goTo(0);
    this._bindEvents();
  }

  _bindEvents() {
    this.tunnel.addEventListener('click', (e) => {
      // Frozen option — show bluff tooltip instead of selecting.
      const frozen = e.target.closest('[data-tunnel-frozen="true"]');
      if (frozen) {
        e.preventDefault();
        e.stopPropagation();
        this._showFrozenTooltip(frozen);
        return;
      }

      const option = e.target.closest(this.config.selectors.option);
      if (option) {
        this._handleOptionClick(option);
        return;
      }

      const step = e.target.closest(this.config.selectors.step);
      if (step) this._handleStepClick(step);
    });

    // Keyboard accessibility — Enter / Space on focused step.
    this.steps.forEach((step, index) => {
      step.setAttribute('role', 'button');
      step.setAttribute('tabindex', '0');
      step.setAttribute('aria-label', `Revenir à l'étape ${index + 1}`);
      step.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._handleStepClick(step);
        }
      });
    });

    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this._goBack());
    }
  }

  _handleStepClick(step) {
    const index = this.steps.indexOf(step);
    if (index === -1) return;

    // Only allow navigating to completed or current steps, never forward
    // to an uncompleted step (user must answer in order).
    if (index <= this.currentIndex) {
      this._goTo(index);
    }
  }

  /**
   * Show a bluff tooltip on a frozen option (e.g. "Immédiatement").
   * The tooltip is portalled to document.body so it escapes any
   * ancestor overflow:hidden clipping (the tunnel panels wrapper).
   * It contains an "unfreeze" button that removes the frozen state
   * and auto-selects the option as an Easter egg.
   */
  _showFrozenTooltip(option) {
    // Remove any existing tooltip first.
    document.querySelectorAll('.wleb-tunnel__frozen-tip').forEach((t) => t.remove());

    const tip = document.createElement('div');
    tip.className = 'wleb-tunnel__frozen-tip';
    tip.setAttribute('role', 'dialog');
    tip.innerHTML = `
      <p class="wleb-tunnel__frozen-tip-title">Aïe…</p>
      <p class="wleb-tunnel__frozen-tip-body">On aimerait bien, mais notre planning déborde déjà. Nos délais réalistes commencent plutôt à <strong>1 mois</strong>.</p>
      <div class="wleb-tunnel__frozen-tip-actions">
        <button type="button" class="wleb-tunnel__frozen-tip-btn wleb-tunnel__frozen-tip-btn--ghost" data-frozen-close>D'accord, je choisis autre chose</button>
        <button type="button" class="wleb-tunnel__frozen-tip-btn wleb-tunnel__frozen-tip-btn--primary" data-frozen-unfreeze>Tant pis, je choisis quand même</button>
      </div>
    `;

    // Portal to body so overflow:hidden on ancestors doesn't clip it.
    document.body.appendChild(tip);
    option.classList.add('is-tip-open');

    const positionTip = () => {
      const rect = option.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      const margin = 12;
      const viewportW = window.innerWidth;

      // Default below the option, centered horizontally.
      let top = rect.bottom + margin;
      let left = rect.left + rect.width / 2 - tipRect.width / 2;

      // Flip above if not enough space below.
      const flipAbove = top + tipRect.height > window.innerHeight - 16;
      if (flipAbove) {
        top = rect.top - tipRect.height - margin;
        tip.classList.add('is-above');
      } else {
        tip.classList.remove('is-above');
      }

      // Clamp horizontally to viewport.
      left = Math.max(16, Math.min(left, viewportW - tipRect.width - 16));

      tip.style.top  = `${top + window.scrollY}px`;
      tip.style.left = `${left + window.scrollX}px`;

      // Arrow horizontal offset (relative to tip) to stay under the option center.
      const arrowX = rect.left + rect.width / 2 - left;
      tip.style.setProperty('--wleb-tip-arrow-x', `${arrowX}px`);
    };

    // Position before transition, then animate in.
    positionTip();
    requestAnimationFrame(() => {
      positionTip(); // re-measure now that tip is in DOM
      tip.classList.add('is-visible');
    });

    const reposition = () => positionTip();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    const close = () => {
      tip.classList.remove('is-visible');
      option.classList.remove('is-tip-open');
      setTimeout(() => tip.remove(), 200);
      document.removeEventListener('click', outsideClick, true);
      document.removeEventListener('keydown', escClose);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };

    const outsideClick = (e) => {
      if (!tip.contains(e.target) && e.target !== option && !option.contains(e.target)) {
        close();
      }
    };

    const escClose = (e) => {
      if (e.key === 'Escape') close();
    };

    tip.querySelector('[data-frozen-close]').addEventListener('click', (e) => {
      e.stopPropagation();
      close();
    });

    tip.querySelector('[data-frozen-unfreeze]').addEventListener('click', (e) => {
      e.stopPropagation();
      close();
      // Unfreeze the option permanently for this session and select it.
      option.removeAttribute('data-tunnel-frozen');
      option.classList.add('is-unfrozen');
      this._handleOptionClick(option);
    });

    // Delay outside-click listener so the current click doesn't close it.
    setTimeout(() => {
      document.addEventListener('click', outsideClick, true);
      document.addEventListener('keydown', escClose);
    }, 0);
  }

  /* ------------------------------------------------------------------ */

  _handleOptionClick(option) {
    const panel = option.closest(this.config.selectors.panel);
    const panelIndex = this.panels.indexOf(panel);

    // Deselect siblings
    panel.querySelectorAll(this.config.selectors.option).forEach((o) => {
      o.removeAttribute('data-selected');
    });

    // Select clicked
    option.setAttribute('data-selected', '');

    // Store answer
    const key = panel.getAttribute('data-tunnel-key');
    const value = option.getAttribute('data-tunnel-value') || option.textContent.trim();
    this.answers[key] = value;

    // Inject into hidden field if it exists
    const hidden = this.tunnel.querySelector(`input[name="${key}"]`);
    if (hidden) hidden.value = value;

    // Auto-advance after delay
    setTimeout(() => {
      if (panelIndex < this.panels.length - 1) {
        this._goTo(panelIndex + 1);
      }
    }, this.config.autoAdvanceDelay);
  }

  /* ------------------------------------------------------------------ */

  _goTo(index) {
    const previous = this.currentIndex;
    this.currentIndex = index;

    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.setAttribute('data-state', 'active');
      } else if (i < index) {
        panel.setAttribute('data-state', 'exit-left');
      } else {
        panel.removeAttribute('data-state');
      }
    });

    // Update step indicators
    this.steps.forEach((step, i) => {
      if (i < index) {
        step.setAttribute('data-state', 'completed');
      } else if (i === index) {
        step.setAttribute('data-state', 'active');
      } else {
        step.removeAttribute('data-state');
      }
    });

    // Update progress bar
    const progress = this.panels.length > 1
      ? (index / (this.panels.length - 1)) * 100
      : 0;
    if (this.progressBar) {
      this.progressBar.style.width = `${progress}%`;
    }

    // Show/hide back button
    if (this.backBtn) {
      if (index > 0) {
        this.backBtn.setAttribute('data-visible', '');
      } else {
        this.backBtn.removeAttribute('data-visible');
      }
    }
  }

  _goBack() {
    if (this.currentIndex > 0) {
      this._goTo(this.currentIndex - 1);
    }
  }

  /* ------------------------------------------------------------------ */

  destroy() {
    // Event listeners are bound to the tunnel container, so removing the
    // DOM node is sufficient. No global listeners to clean up.
  }
}
